import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "garage-door-web-design-colorado",
  metaTitle: "Garage Door Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom garage door web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Garage door marketing", to: "/garage-door-marketing-colorado" },
  ],
  currentLabel: "Garage Door Web Design Colorado",
  eyebrow: "Garage Door Web Design — Colorado",
  h1: (
    <>
      Garage Door <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded garage door websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Garage Door websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first garage door sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Garage Door web design in Colorado",
    body: "Garage Door web design is the practice of building conversion-focused, mobile-first websites for garage door companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — broken garage door is a same-day emergency search." },
    { title: "Service pages per job type", body: "Spring replacement, opener repair, panel replacement, new install, off-track — each ranks independently." },
    { title: "Sticky click-to-call", body: "Phone visible on every scroll — most calls happen from the homeowner standing in the driveway." },
    { title: "Same-day service messaging", body: "Real-time technician availability and same-day booking surfaced above the fold." },
    { title: "Brand & product pages", body: "LiftMaster, Clopay, Wayne Dalton, Genie — branded pages capture branded search volume." },
    { title: "Schema and SEO baked in", body: "LocalBusiness, Service, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Same-day emergency intent dominates", body: "Broken spring or off-track door — these are now-or-never calls. Speed wins or loses." },
    { heading: "Branded searches convert higher", body: "LiftMaster repair, Clopay installation — branded queries convert 3x higher than generic." },
    { heading: "Most garage door sites are slow", body: "Template sites lose the Map Pack call to the next listing. Hand-coded wins by default." },
  ],
  relatedLinks: [
    { to: "/garage-door-marketing-colorado", label: "Garage door marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado garage door case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a garage door website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a garage door website?", a: "Typical timeline is 4–6 weeks for a 12–20 page garage door site with full service and service-area pages. Larger multi-location garage door companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs garage door companies?", a: "No — we build garage door sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Garage Door Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for garage door companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/garage-door-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
